// Cooking timers. Stored as end times (not countdowns) so they stay accurate
// when the tab is in the background or the app is reopened.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { newId } from '@dishly/shared';

export interface CookingTimer {
  id: string;
  label: string;
  recipeName: string;
  recipePath: string;
  recipeId: string;
  stepIndex: number;
  durationMs: number;
  /** Set while running. */
  endsAt: number | null;
  /** Set while paused. */
  remainingMs: number;
  status: 'running' | 'paused' | 'done';
}

export const useTimers = create<{ timers: CookingTimer[] }>()(
  persist(() => ({ timers: [] as CookingTimer[] }), { name: 'dishly-timers', version: 1 }),
);

function update(id: string, change: (timer: CookingTimer) => CookingTimer | null): void {
  useTimers.setState((s) => ({
    timers: s.timers.flatMap((timer) => {
      if (timer.id !== id) return [timer];
      const next = change(timer);
      return next ? [next] : [];
    }),
  }));
}

export function startTimer(input: Pick<CookingTimer, 'label' | 'recipeName' | 'recipePath' | 'recipeId' | 'stepIndex'> & { minutes: number }): void {
  unlockAudio();
  if ('Notification' in window && Notification.permission === 'default') void Notification.requestPermission();
  const durationMs = Math.round(input.minutes * 60_000);
  const timer: CookingTimer = {
    id: newId(),
    label: input.label,
    recipeName: input.recipeName,
    recipePath: input.recipePath,
    recipeId: input.recipeId,
    stepIndex: input.stepIndex,
    durationMs,
    endsAt: Date.now() + durationMs,
    remainingMs: durationMs,
    status: 'running',
  };
  // Restarting a step's timer replaces the old one.
  useTimers.setState((s) => ({
    timers: [...s.timers.filter((t) => !(t.recipeId === input.recipeId && t.stepIndex === input.stepIndex)), timer],
  }));
}

export function pauseTimer(id: string): void {
  update(id, (t) => (t.endsAt ? { ...t, status: 'paused', remainingMs: Math.max(0, t.endsAt - Date.now()), endsAt: null } : t));
}

export function resumeTimer(id: string): void {
  unlockAudio();
  update(id, (t) => ({ ...t, status: 'running', endsAt: Date.now() + t.remainingMs }));
}

export function removeTimer(id: string): void {
  update(id, () => null);
}

export function remainingMs(timer: CookingTimer, now = Date.now()): number {
  if (timer.status === 'done') return 0;
  return timer.endsAt ? Math.max(0, timer.endsAt - now) : timer.remainingMs;
}

/** Marks finished timers as done; returns the ones that just finished. */
export function collectFinished(now = Date.now()): CookingTimer[] {
  const finished = useTimers.getState().timers.filter((t) => t.status === 'running' && t.endsAt !== null && t.endsAt <= now);
  for (const timer of finished) update(timer.id, (t) => ({ ...t, status: 'done', endsAt: null, remainingMs: 0 }));
  return finished;
}

export function formatClock(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

let audio: AudioContext | null = null;

/** Browsers only allow sound after a user gesture, so call this from a click. */
export function unlockAudio(): void {
  try {
    audio ??= new AudioContext();
    if (audio.state === 'suspended') void audio.resume();
  } catch {
    audio = null;
  }
}

export function playAlarm(): void {
  if (!audio) return;
  const start = audio.currentTime;
  for (let i = 0; i < 3; i++) {
    const oscillator = audio.createOscillator();
    const gain = audio.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = i === 2 ? 1046 : 880;
    oscillator.connect(gain);
    gain.connect(audio.destination);
    const t0 = start + i * 0.3;
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.35, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.24);
    oscillator.start(t0);
    oscillator.stop(t0 + 0.26);
  }
}

export async function notify(title: string, body: string, url: string, tag: string): Promise<void> {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const options: NotificationOptions = { body, tag, icon: '/icons/icon-192.png', data: { url }, requireInteraction: true };
  try {
    const registration = await navigator.serviceWorker?.getRegistration();
    if (registration) await registration.showNotification(title, options);
    else new Notification(title, options);
  } catch {
    // Notifications are a nice-to-have; the in-app alarm still shows.
  }
}
